import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as BackIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import loteService, { ProductoLote } from '../../services/loteService';
import loteInformesService from '../../services/loteInformesService';
import type { LoteMovimientoDto, LoteTrazabilidad } from '../../types/lotes-informes';
import LoteAdjuntosPanel from '../../components/lotes/LoteAdjuntosPanel';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  labelLoteMovimientoDocumento,
  resolveLoteMovimientoPath,
} from '../../utils/loteMovimientoLinks';

const LoteDetallePage: React.FC = () => {
  const { idLote } = useParams<{ idLote: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [lote, setLote] = useState<ProductoLote | null>(null);
  const [movimientos, setMovimientos] = useState<LoteMovimientoDto[]>([]);
  const [trazabilidad, setTrazabilidad] = useState<LoteTrazabilidad | null>(null);
  const [estado, setEstado] = useState('A');
  const [vencimiento, setVencimiento] = useState('');
  const [observaciones, setObservaciones] = useState('');

  const id = Number(idLote);

  const load = async () => {
    if (!id) return;
    try {
      const l = await loteService.getById(id);
      setLote(l);
      setEstado(l.estado);
      setVencimiento(l.fechaVencimiento ? String(l.fechaVencimiento).slice(0, 10) : '');
      setObservaciones(l.observaciones || '');
      const movs = await loteInformesService.movimientos({ idLote: id, pageSize: 100 });
      setMovimientos(movs.items);
      setTrazabilidad(await loteInformesService.trazabilidad(id));
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo cargar el lote'), { variant: 'error' });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSave = async () => {
    try {
      await loteService.update(id, {
        estado,
        fechaVencimiento: vencimiento || null,
        observaciones,
      });
      enqueueSnackbar('Lote actualizado', { variant: 'success' });
      await load();
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo guardar'), { variant: 'error' });
    }
  };

  if (!lote) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Cargando…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Stack spacing={2}>
        <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button startIcon={<BackIcon />} onClick={() => navigate('/inventario/lotes')}>
                  Volver
                </Button>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  {lote.nroLote}
                </Typography>
                {lote.esSerie && <Chip label="Serie" size="small" />}
              </Stack>
              <Button variant="contained" onClick={handleSave}>
                Guardar
              </Button>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Artículo
                </Typography>
                <Typography fontWeight={600}>
                  {lote.codigoArticulo} — {lote.descripcionArticulo}
                </Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Estado"
                  value={estado}
                  onChange={(e) => setEstado(e.target.value)}
                >
                  <MenuItem value="A">Activo</MenuItem>
                  <MenuItem value="B">Bloqueado</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Vencimiento"
                  InputLabelProps={{ shrink: true }}
                  value={vencimiento}
                  onChange={(e) => setVencimiento(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Observaciones"
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
              Saldos por depósito
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Depósito</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lote.depositos.map((d) => (
                  <TableRow key={d.nroUbicacion}>
                    <TableCell>
                      {d.nroUbicacion} — {d.nombreUbicacion}
                    </TableCell>
                    <TableCell align="right">{d.cantidad}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <LoteAdjuntosPanel idLote={id} />
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Movimientos
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Depósito</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell>Documento</TableCell>
                  <TableCell>Cliente / Prov.</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movimientos.map((m) => {
                  const docLabel = labelLoteMovimientoDocumento(m);
                  const docPath = resolveLoteMovimientoPath(m);
                  return (
                    <TableRow key={m.idMovimiento} hover>
                      <TableCell>{new Date(m.fecha).toLocaleString()}</TableCell>
                      <TableCell>{m.tipoDocumento || m.tipoMov}</TableCell>
                      <TableCell>
                        {m.nroUbicacion} {m.nombreUbicacion}
                      </TableCell>
                      <TableCell align="right">{m.cantidad}</TableCell>
                      <TableCell>
                        {docPath && docLabel !== '—' ? (
                          <Link
                            component={RouterLink}
                            to={docPath}
                            underline="hover"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              fontWeight: 600,
                            }}
                          >
                            {docLabel}
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </Link>
                        ) : (
                          docLabel
                        )}
                      </TableCell>
                      <TableCell>
                        {[m.nroTercero, m.nombreTercero].filter(Boolean).join(' · ') || '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {trazabilidad && (
          <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Trazabilidad / recall (clientes)
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Contacto</TableCell>
                    <TableCell>Comprobante</TableCell>
                    <TableCell align="right">Cant. neta</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trazabilidad.ventas.map((v) => (
                    <TableRow key={`${v.nroIDFormulario}-${v.nroCliente}`}>
                      <TableCell>
                        {v.nroCliente} — {v.nombreCliente}
                      </TableCell>
                      <TableCell>
                        {v.telefono || '—'} / {v.email || '—'}
                      </TableCell>
                      <TableCell>
                        {v.letra}-{v.nroSuc}-{v.nroComprobante}
                      </TableCell>
                      <TableCell align="right">{v.cantidadNeta}</TableCell>
                    </TableRow>
                  ))}
                  {trazabilidad.ventas.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          Sin ventas asociadas
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {(trazabilidad.genealogia.origenes.length > 0 ||
                trazabilidad.genealogia.destinos.length > 0) && (
                <Stack spacing={1} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Genealogía de producción</Typography>
                  <Typography variant="body2">
                    Orígenes:{' '}
                    {trazabilidad.genealogia.origenes.map((o) => `${o.codigoArticulo}/${o.nroLote}`).join(', ') ||
                      '—'}
                  </Typography>
                  <Typography variant="body2">
                    Destinos:{' '}
                    {trazabilidad.genealogia.destinos.map((d) => `${d.codigoArticulo}/${d.nroLote}`).join(', ') ||
                      '—'}
                  </Typography>
                </Stack>
              )}
            </CardContent>
          </Card>
        )}
      </Stack>
    </Box>
  );
};

export default LoteDetallePage;
